import { useEffect, useState, useCallback } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  LayoutDashboard,
  Users,
  Briefcase,
  FileText,
  Rocket,
  Image as ImageIcon,
  Mail,
  LogOut,
  Trash2,
  Plus,
  Languages,
  GripVertical,
  Award,
  ScrollText,
  Upload,
  Type,
  Building2,
  Sprout,
} from "lucide-react";
import TranslationsEditor from "@/components/TranslationsEditor";
import RecruitmentsAdmin from "@/components/admin/RecruitmentsAdmin";
import ChipmatAdmin from "@/components/admin/ChipmatAdmin";
import SiteContentAdmin from "@/components/admin/SiteContentAdmin";
import InitiativesAdmin from "@/components/admin/InitiativesAdmin";
import IncubationAdmin from "@/components/admin/IncubationAdmin";
import { confirmRemove } from "@/lib/confirm";

const SECTIONS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "sitecontent", label: "Site Content", icon: Type },
  { key: "board", label: "Board Members", icon: Users },
  { key: "initiatives", label: "Initiatives", icon: Building2 },
  { key: "startups", label: "Startups", icon: Rocket },
  { key: "recruitments", label: "Recruitment Notices", icon: Briefcase },
  { key: "ranklists", label: "Rank Lists", icon: Award },
  { key: "tenders", label: "Tenders & Notices", icon: ScrollText },
  { key: "chipmat", label: "CHIPMAT Applications", icon: FileText },
  { key: "incubation", label: "Incubation Applications", icon: Sprout },
  { key: "gallery", label: "Gallery", icon: ImageIcon },
  { key: "messages", label: "Messages", icon: Mail },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [section, setSection] = useState("overview");
  const [counts, setCounts] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/board-members").catch(() => ({ data: [] })),
      api.get("/startups").catch(() => ({ data: [] })),
      api.get("/recruitments").catch(() => ({ data: [] })),
      api.get("/chipmat/applications").catch(() => ({ data: [] })),
      api.get("/gallery").catch(() => ({ data: [] })),
      api.get("/contact").catch(() => ({ data: [] })),
      api.get("/rank-lists").catch(() => ({ data: [] })),
      api.get("/tenders").catch(() => ({ data: [] })),
      api.get("/incubation/applications").catch(() => ({ data: [] })),
    ]).then(([b, s, j, a, g, m, rl, td, inc]) => {
      setCounts({
        board: b.data.length,
        startups: s.data.length,
        recruitments: j.data.length,
        chipmat: a.data.length,
        gallery: g.data.length,
        messages: m.data.length,
        ranklists: rl.data.length,
        tenders: td.data.length,
        incubation: inc.data.length,
      });
    });
  }, [section]);

  return (
    <div className="min-h-screen flex bg-white text-ink">
      <Toaster richColors position="top-right" />
      {/* Sidebar */}
      <aside className="w-64 border-r border-ink/10 bg-surface flex flex-col">
        <div className="p-6 border-b border-ink/10">
          <Link to="/" className="flex items-center gap-3">
            <img src="/semicon-keralam-logo.png" alt="Semicon Keralam" className="h-10 w-auto" />
            <div className="border-l border-ink/15 pl-3">
              <div className="font-mono text-[9px] uppercase tracking-widest text-ink/50">Admin</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-ink/40">Console</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 py-4">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                data-testid={`admin-nav-${s.key}`}
                onClick={() => setSection(s.key)}
                className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                  section === s.key ? "text-teal bg-ink/5 border-l-2 border-teal" : "text-ink/70 hover:text-ink hover:bg-ink/5"
                }`}
              >
                <Icon className="w-4 h-4" /> {s.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-ink/10">
          <div className="text-xs text-ink/50 truncate mb-2">{user?.email}</div>
          <button
            data-testid="admin-logout"
            onClick={async () => { await logout(); nav("/"); }}
            className="w-full flex items-center gap-2 border border-ink/10 hover:border-teal px-3 py-2 text-sm"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        <header className="border-b border-ink/10 p-6">
          <div className="tag mb-1">// admin console</div>
          <h1 className="font-display font-black text-brand text-2xl capitalize">{SECTIONS.find((s) => s.key === section)?.label || section}</h1>
        </header>
        <div className="p-6">
          {section === "overview" && <Overview counts={counts} />}
          {section === "sitecontent" && <SiteContentAdmin />}
          {section === "board" && <BoardAdmin />}
          {section === "initiatives" && <InitiativesAdmin />}
          {section === "startups" && <StartupsAdmin />}
          {section === "recruitments" && <RecruitmentsAdmin />}
          {section === "ranklists" && <RankListsAdmin />}
          {section === "tenders" && <TendersAdmin />}
          {section === "chipmat" && <ChipmatAdmin />}
          {section === "incubation" && <IncubationAdmin />}
          {section === "gallery" && <GalleryAdmin />}
          {section === "messages" && <MessagesAdmin />}
        </div>
      </main>
    </div>
  );
}

function Overview({ counts }) {
  const items = [
    { label: "Board members", value: counts.board },
    { label: "Startups", value: counts.startups },
    { label: "Recruitment notices", value: counts.recruitments },
    { label: "CHIPMAT applications", value: counts.chipmat },
    { label: "Incubation applications", value: counts.incubation },
    { label: "Gallery items", value: counts.gallery },
    { label: "Rank lists", value: counts.ranklists },
    { label: "Tenders & notices", value: counts.tenders },
    { label: "Messages", value: counts.messages },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-ink/10">
      {items.map((it) => (
        <div key={it.label} className="bg-base p-6">
          <div className="font-display font-black text-ink text-4xl">{it.value ?? "…"}</div>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-ink/50">{it.label}</div>
        </div>
      ))}
    </div>
  );
}

function useList(endpoint, initial = []) {
  const [items, setItems] = useState(initial);
  const refresh = useCallback(() => {
    api.get(endpoint).then((r) => setItems(r.data)).catch(() => {});
  }, [endpoint]);
  useEffect(() => { refresh(); }, [refresh]);
  return [items, refresh];
}

function BoardAdmin() {
  const [items, refresh] = useList("/board-members");
  const [form, setForm] = useState({ name: "", title: "", affiliation: "", photo_url: "", order: 100 });
  const [trDoc, setTrDoc] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/board-members", form);
      setForm({ name: "", title: "", affiliation: "", photo_url: "", order: 100 });
      refresh();
      toast.success("Board member added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    await api.delete(`/board-members/${id}`);
    refresh();
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={submit} className="lg:col-span-1 border border-ink/10 bg-surface p-6 space-y-3">
        <div className="tag mb-2">// add member</div>
        {[
          ["name", "Name"], ["title", "Title"], ["affiliation", "Affiliation"], ["photo_url", "Photo URL"],
        ].map(([k, label]) => (
          <div key={k}>
            <label className="block text-xs font-mono uppercase tracking-widest text-ink/50 mb-1">{label}</label>
            <input
              data-testid={`board-input-${k}`}
              required={k !== "photo_url"}
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              className="w-full bg-base border border-ink/10 px-3 py-2 text-ink text-sm"
            />
          </div>
        ))}
        <button type="submit" data-testid="board-add-submit" className="w-full bg-brand hover:bg-brand-dark text-ink py-2.5 flex items-center justify-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> Add
        </button>
      </form>

      <div className="lg:col-span-2 border border-ink/10 bg-surface">
        <table className="w-full text-sm">
          <thead className="border-b border-ink/10 text-xs font-mono uppercase tracking-widest text-ink/50">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Affiliation</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id} className="border-b border-ink/5 hover:bg-ink/5">
                <td className="p-3">{m.name}</td>
                <td className="p-3 text-ink/70">{m.title}</td>
                <td className="p-3 text-ink/60 text-xs">{m.affiliation}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-3">
                    <button data-testid={`board-tr-${m.id}`} onClick={() => setTrDoc(m)} className="text-teal hover:text-teal-dark" title="Edit translations" aria-label="Edit translations">
                      <Languages className="w-4 h-4" />
                    </button>
                    <button data-testid={`board-del-${m.id}`} onClick={() => del(m.id)} className="text-red-400 hover:text-red-300" aria-label="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TranslationsEditor
        open={!!trDoc}
        doc={trDoc}
        collection="board-members"
        fields={["title", "affiliation"]}
        onClose={() => setTrDoc(null)}
        onSaved={() => refresh()}
      />
    </div>
  );
}

function StartupsAdmin() {
  const [items, refresh] = useList("/startups");
  const [form, setForm] = useState({ name: "", sector: "", founders: "", description: "", website: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/startups", form);
      setForm({ name: "", sector: "", founders: "", description: "", website: "" });
      refresh();
      toast.success("Startup added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/startups/${id}`); refresh(); };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={submit} className="lg:col-span-1 border border-ink/10 bg-surface p-6 space-y-3">
        <div className="tag mb-2">// add startup</div>
        {[["name","Name"],["sector","Sector"],["founders","Founders"],["website","Website"]].map(([k, l]) => (
          <div key={k}>
            <label className="block text-xs font-mono uppercase tracking-widest text-ink/50 mb-1">{l}</label>
            <input data-testid={`startup-input-${k}`} required={k === "name" || k === "sector"} value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              className="w-full bg-base border border-ink/10 px-3 py-2 text-ink text-sm" />
          </div>
        ))}
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-ink/50 mb-1">Description</label>
          <textarea data-testid="startup-input-desc" rows={4} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full bg-base border border-ink/10 px-3 py-2 text-ink text-sm resize-none" />
        </div>
        <button type="submit" data-testid="startup-add-submit" className="w-full bg-brand hover:bg-brand-dark text-ink py-2.5 text-sm">Add startup</button>
      </form>
      <div className="lg:col-span-2 border border-ink/10 bg-surface max-h-[70vh] overflow-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-ink/10 text-xs font-mono uppercase tracking-widest text-ink/50 sticky top-0 bg-surface">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Sector</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-ink/5 hover:bg-ink/5">
                <td className="p-3">{s.name}</td>
                <td className="p-3 text-ink/60">{s.sector}</td>
                <td className="p-3 text-right">
                  <button data-testid={`startup-del-${s.id}`} onClick={() => del(s.id)} className="text-red-400 hover:text-red-300">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GalleryAdmin() {
  const [items, refresh] = useList("/gallery");
  const [form, setForm] = useState({ url: "", caption: "" });
  const [file, setFile] = useState(null);
  const [fileCaption, setFileCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [ordered, setOrdered] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => { setOrdered(items); }, [items]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/gallery", form);
      setForm({ url: "", caption: "" });
      refresh();
      toast.success("Image added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose an image file"); return; }
    const fd = new FormData();
    fd.append("image", file);
    fd.append("caption", fileCaption);
    setUploading(true);
    try {
      await api.post("/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(null);
      setFileCaption("");
      e.target.reset();
      refresh();
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const del = async (id) => { await api.delete(`/gallery/${id}`); refresh(); };

  const onDragStart = (i) => setDragIndex(i);
  const onDragOver = (e, i) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) return;
    setOrdered((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setDragIndex(i);
  };
  const onDrop = async () => {
    setDragIndex(null);
    try {
      await api.put("/gallery/reorder", { ids: ordered.map((g) => g.id) });
      toast.success("Order saved");
      refresh();
    } catch (err) {
      toast.error("Could not save order");
      refresh();
    }
  };

  return (
    <div>
      <form onSubmit={upload} className="border border-teal/30 bg-teal/5 p-6 mb-4 grid md:grid-cols-3 gap-3 items-center">
        <div className="md:col-span-3 text-xs font-mono uppercase tracking-widest text-teal">Upload photo from device</div>
        <input data-testid="gallery-file" type="file" accept="image/*" required
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="bg-base border border-ink/10 px-3 py-2 text-ink text-sm file:mr-3 file:border-0 file:bg-brand file:text-white file:px-3 file:py-1" />
        <input data-testid="gallery-file-caption" placeholder="Caption (optional)" value={fileCaption}
          onChange={(e) => setFileCaption(e.target.value)}
          className="bg-base border border-ink/10 px-3 py-2 text-ink text-sm" />
        <button type="submit" disabled={uploading} data-testid="gallery-upload-submit"
          className="bg-teal hover:bg-teal-dark text-white text-sm py-2 disabled:opacity-60">
          {uploading ? "Uploading…" : "Upload image"}
        </button>
      </form>

      <form onSubmit={submit} className="border border-ink/10 bg-surface p-6 mb-6 grid md:grid-cols-3 gap-3 items-center">
        <div className="md:col-span-3 text-xs font-mono uppercase tracking-widest text-ink/50">Or add by image URL</div>
        <input data-testid="gallery-url" placeholder="Image URL" required value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
          className="bg-base border border-ink/10 px-3 py-2 text-ink text-sm" />
        <input data-testid="gallery-caption" placeholder="Caption" value={form.caption}
          onChange={(e) => setForm({ ...form, caption: e.target.value })}
          className="bg-base border border-ink/10 px-3 py-2 text-ink text-sm" />
        <button type="submit" data-testid="gallery-add-submit" className="bg-brand hover:bg-brand-dark text-white text-sm py-2">Add</button>
      </form>

      <p className="text-xs text-ink/50 mb-3 flex items-center gap-1.5">
        <GripVertical className="w-3.5 h-3.5" /> Drag photos to reorder how they appear on the public gallery.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="gallery-grid">
        {ordered.map((g, i) => (
          <div
            key={g.id}
            data-testid={`gallery-item-${g.id}`}
            draggable
            onDragStart={() => onDragStart(i)}
            onDragOver={(e) => onDragOver(e, i)}
            onDrop={onDrop}
            onDragEnd={() => setDragIndex(null)}
            className={`relative group border border-ink/10 bg-white cursor-move transition-opacity ${dragIndex === i ? "opacity-40" : ""}`}
          >
            <div className="absolute top-2 left-2 z-10 bg-black/50 text-white p-1 rounded-sm opacity-0 group-hover:opacity-100">
              <GripVertical className="w-3.5 h-3.5" />
            </div>
            <img src={g.url} alt={g.caption} className="w-full h-40 object-cover pointer-events-none" />
            <button data-testid={`gallery-del-${g.id}`} onClick={() => del(g.id)}
              className="absolute top-2 right-2 bg-red-500/90 text-white p-1.5 opacity-0 group-hover:opacity-100">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <div className="p-2 text-xs text-ink/60 truncate">{g.caption}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PdfUpload({ onUploaded, testid }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post("/uploads/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.url, data.filename);
      toast.success("PDF uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "PDF upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label data-testid={testid} className="shrink-0 inline-flex items-center gap-1.5 text-xs border border-ink/15 px-2.5 py-2 cursor-pointer hover:border-teal text-ink/70">
      <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Upload PDF"}
      <input type="file" accept="application/pdf" className="hidden" onChange={handle} disabled={busy} />
    </label>
  );
}

function RankListsAdmin() {
  const [items, refresh] = useList("/rank-lists");
  const empty = { title: "", link: "", date: "" };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/rank-lists/${editId}`, form);
      else await api.post("/rank-lists", form);
      setForm(empty); setEditId(null); refresh();
      toast.success(editId ? "Rank list updated" : "Rank list added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
  };
  const edit = (it) => { setEditId(it.id); setForm({ title: it.title || "", link: it.link || "", date: it.date || "" }); };
  const del = async (id) => { if (!confirmRemove("this rank list")) return; await api.delete(`/rank-lists/${id}`); if (editId === id) { setForm(empty); setEditId(null); } refresh(); };

  return (
    <div>
      <form onSubmit={submit} className="border border-ink/10 bg-surface p-6 mb-6 grid gap-3">
        <div className="text-xs font-mono uppercase tracking-widest text-ink/50">{editId ? "Edit rank list" : "Add rank list"}</div>
        <textarea data-testid="ranklist-title" placeholder="Title / description" required rows={2} value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-base border border-ink/10 px-3 py-2 text-ink text-sm" />
        <div className="grid md:grid-cols-2 gap-3">
          <div className="flex gap-2">
            <input data-testid="ranklist-link" placeholder="PDF / document URL" value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })} className="flex-1 bg-base border border-ink/10 px-3 py-2 text-ink text-sm" />
            <PdfUpload testid="ranklist-pdf" onUploaded={(url) => setForm((f) => ({ ...f, link: url }))} />
          </div>
          <input data-testid="ranklist-date" placeholder="Year / date (e.g. 2026)" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-base border border-ink/10 px-3 py-2 text-ink text-sm" />
        </div>
        <div className="flex gap-2">
          <button type="submit" data-testid="ranklist-submit" className="bg-brand hover:bg-brand-dark text-white text-sm px-5 py-2">{editId ? "Update" : "Add"}</button>
          {editId && <button type="button" onClick={() => { setForm(empty); setEditId(null); }} className="border border-ink/15 text-ink/70 text-sm px-4 py-2">Cancel</button>}
        </div>
      </form>
      <ul className="space-y-2 list-none p-0" data-testid="ranklist-admin-list">
        {items.map((it) => (
          <li key={it.id} data-testid={`ranklist-row-${it.id}`} className="border border-ink/10 bg-white p-4 flex items-start gap-4">
            <div className="flex-1">
              <p className="text-ink/85 text-sm">{it.title}</p>
              <div className="mt-1 flex items-center gap-3 font-mono text-[10px] text-ink/50">
                {it.date && <span>{it.date}</span>}
                {it.link && <a href={it.link} target="_blank" rel="noreferrer" className="text-teal underline">PDF</a>}
              </div>
            </div>
            <button onClick={() => edit(it)} data-testid={`ranklist-edit-${it.id}`} className="text-ink/60 hover:text-brand text-xs border border-ink/10 px-2 py-1">Edit</button>
            <button onClick={() => del(it.id)} data-testid={`ranklist-del-${it.id}`} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TendersAdmin() {
  const [items, refresh] = useList("/tenders");
  const empty = { title: "", notification_link: "", corrigendum_link: "", apply_link: "", last_date: "" };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/tenders/${editId}`, form);
      else await api.post("/tenders", form);
      setForm(empty); setEditId(null); refresh();
      toast.success(editId ? "Tender updated" : "Tender added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
  };
  const edit = (it) => { setEditId(it.id); setForm({ title: it.title || "", notification_link: it.notification_link || "", corrigendum_link: it.corrigendum_link || "", apply_link: it.apply_link || "", last_date: it.last_date || "" }); };
  const del = async (id) => { if (!confirmRemove("this tender / notice")) return; await api.delete(`/tenders/${id}`); if (editId === id) { setForm(empty); setEditId(null); } refresh(); };

  return (
    <div>
      <form onSubmit={submit} className="border border-ink/10 bg-surface p-6 mb-6 grid gap-3">
        <div className="text-xs font-mono uppercase tracking-widest text-ink/50">{editId ? "Edit tender / notice" : "Add tender / notice"}</div>
        <textarea data-testid="tender-title" placeholder="Title / description" required rows={2} value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-base border border-ink/10 px-3 py-2 text-ink text-sm" />
        <div className="grid md:grid-cols-2 gap-3">
          <div className="flex gap-2">
            <input data-testid="tender-notification" placeholder="Notification PDF URL" value={form.notification_link}
              onChange={(e) => setForm({ ...form, notification_link: e.target.value })} className="flex-1 bg-base border border-ink/10 px-3 py-2 text-ink text-sm" />
            <PdfUpload testid="tender-notification-pdf" onUploaded={(url) => setForm((f) => ({ ...f, notification_link: url }))} />
          </div>
          <div className="flex gap-2">
            <input data-testid="tender-corrigendum" placeholder="Corrigendum URL (optional)" value={form.corrigendum_link}
              onChange={(e) => setForm({ ...form, corrigendum_link: e.target.value })} className="flex-1 bg-base border border-ink/10 px-3 py-2 text-ink text-sm" />
            <PdfUpload testid="tender-corrigendum-pdf" onUploaded={(url) => setForm((f) => ({ ...f, corrigendum_link: url }))} />
          </div>
          <input data-testid="tender-apply" placeholder="Apply / e-Tender URL (optional)" value={form.apply_link}
            onChange={(e) => setForm({ ...form, apply_link: e.target.value })} className="bg-base border border-ink/10 px-3 py-2 text-ink text-sm" />
          <input data-testid="tender-lastdate" placeholder="Last date — dd/mm/yyyy (optional)" value={form.last_date}
            onChange={(e) => setForm({ ...form, last_date: e.target.value })} className="bg-base border border-ink/10 px-3 py-2 text-ink text-sm" />
        </div>
        <div className="flex gap-2">
          <button type="submit" data-testid="tender-submit" className="bg-brand hover:bg-brand-dark text-white text-sm px-5 py-2">{editId ? "Update" : "Add"}</button>
          {editId && <button type="button" onClick={() => { setForm(empty); setEditId(null); }} className="border border-ink/15 text-ink/70 text-sm px-4 py-2">Cancel</button>}
        </div>
      </form>
      <ul className="space-y-2 list-none p-0" data-testid="tender-admin-list">
        {items.map((it) => (
          <li key={it.id} data-testid={`tender-row-${it.id}`} className="border border-ink/10 bg-white p-4 flex items-start gap-4">
            <div className="flex-1">
              <p className="text-ink/85 text-sm">{it.title}</p>
              <div className="mt-1 flex items-center gap-3 font-mono text-[10px] text-ink/50 flex-wrap">
                {it.last_date && <span>Last date: {it.last_date}</span>}
                {it.notification_link && <a href={it.notification_link} target="_blank" rel="noreferrer" className="text-teal underline">Notification</a>}
                {it.corrigendum_link && <a href={it.corrigendum_link} target="_blank" rel="noreferrer" className="text-teal underline">Corrigendum</a>}
                {it.apply_link && <a href={it.apply_link} target="_blank" rel="noreferrer" className="text-teal underline">Apply</a>}
              </div>
            </div>
            <button onClick={() => edit(it)} data-testid={`tender-edit-${it.id}`} className="text-ink/60 hover:text-brand text-xs border border-ink/10 px-2 py-1">Edit</button>
            <button onClick={() => del(it.id)} data-testid={`tender-del-${it.id}`} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MessagesAdmin() {
  const [items, refresh] = useList("/contact");
  const del = async (id) => { await api.delete(`/contact/${id}`); refresh(); };
  return (
    <div className="space-y-3">
      {items.map((m) => (
        <div key={m.id} className="border border-ink/10 bg-surface p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-ink/50">{m.submitted_at?.substring(0, 16).replace("T", " ")}</div>
              <div className="font-display font-bold text-ink">{m.subject}</div>
              <div className="text-ink/60 text-sm">{m.name} · <a href={`mailto:${m.email}`} className="text-teal">{m.email}</a></div>
            </div>
            <button data-testid={`msg-del-${m.id}`} onClick={() => del(m.id)} className="text-red-400 hover:text-red-300">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <p className="mt-3 text-ink/80 text-sm whitespace-pre-line">{m.message}</p>
        </div>
      ))}
      {items.length === 0 && <div className="text-ink/50 text-sm">No messages yet.</div>}
    </div>
  );
}
